import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  SmokingSessionStatus,
  attachCurrentSmokerSnapshot,
  clearSessionSmoker,
  changeActiveSmokingStage,
  createSmokingSession,
  softDeleteSmokingSession,
  startSmokingSession,
  transitionSmokingSession,
  updateSmokingSession
} from './session.js';

export const SMOKING_SESSION_ENTITY_TYPE='smoking_session';
export const EDITABLE_SESSION_STATUSES=Object.freeze([
  SmokingSessionStatus.DRAFT,
  SmokingSessionStatus.PLANNED,
  SmokingSessionStatus.PREPARATION,
  SmokingSessionStatus.READY
]);
const EDITABLE=new Set(EDITABLE_SESSION_STATUSES);

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function clean(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function domain(row){
  const copy=clean(row);
  const domainKeys=[
    'id','userId','status','title','smokerId','smokerSnapshot',
    'recipeId','recipeVersion','recipeSourceSnapshot','recipeUseRequestId','plannerScheduleSnapshot','sourceSessionId',
    'plannedStartAt','plannedEndAt','actualStartAt','actualEndAt',
    'activeStageId','activeStageStartedAt','stageMutationBatchId','statusChangedAt','deletedAt'
  ];
  return createSmokingSession(Object.fromEntries(domainKeys.map(key=>[key,copy[key]])));
}

function merge(existing,value,{now}){
  if(existing){
    return {
      ...clean(existing),
      ...value,
      id:existing.id,
      userId:existing.userId
    };
  }
  return {
    ...createEntityMeta({id:value.id,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...value
  };
}

function generatedMutationBatchId(){
  const value=globalThis.crypto?.randomUUID?.();
  if(value)return value;
  throw new Error('LIVE_STAGE_MUTATION_ID_UNAVAILABLE');
}

function assertEditable(session){
  if(!EDITABLE.has(session.status))throw new Error('SMOKING_SESSION_LOCKED_AFTER_START');
}

export function createSmokingSessionStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString(),
  onLifecycle=()=>{}
}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId');

  function notifyLifecycle(previous,next){
    try{onLifecycle?.(Object.freeze({previous,next}));}catch{}
  }

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('SMOKING_SESSION_OWNER_MISMATCH');
    return row;
  }

  async function get(id){
    const row=await repository.get(SMOKING_SESSION_ENTITY_TYPE,required(id,'id'));
    if(!row)return null;
    return Object.freeze(assertOwner(row));
  }

  async function list({includeDeleted=false,status=null}={}){
    const rows=await repository.list(SMOKING_SESSION_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===owner)
      .filter(row=>includeDeleted||!row.deletedAt)
      .filter(row=>!status||row.status===status)
      .sort((a,b)=>
        String(b.updatedAt||b.createdAt||'').localeCompare(String(a.updatedAt||a.createdAt||''))
        ||String(a.id).localeCompare(String(b.id))
      ));
  }

  async function active(){
    const rows=await list({status:SmokingSessionStatus.SMOKING});
    if(rows.length>1){
      const error=new Error('MULTIPLE_ACTIVE_SMOKING_SESSIONS');
      error.sessionIds=Object.freeze(rows.map(row=>row.id));
      throw error;
    }
    return rows[0]||null;
  }

  async function createDraft(input={}){
    if(input.status&&String(input.status).toLowerCase()!==SmokingSessionStatus.DRAFT){
      throw new TypeError('New journal session must begin as draft');
    }
    const value=createSmokingSession({
      ...input,
      userId:owner,
      status:SmokingSessionStatus.DRAFT,
      statusChangedAt:input.statusChangedAt||now()
    });
    const entity=merge(null,value,{now:now()});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'create');
  }

  async function resumeDraft(){
    const drafts=await list({status:SmokingSessionStatus.DRAFT});
    return drafts[0]||null;
  }

  async function update(id,changes={}){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const currentDomain=domain(current);
    assertEditable(currentDomain);
    const value=updateSmokingSession(currentDomain,changes);
    const entity=merge(current,value,{now:now()});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
  }

  async function setSmoker(id,{smoker,capabilities,capturedAt=now()}={}){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const value=attachCurrentSmokerSnapshot(domain(current),{smoker,capabilities,capturedAt});
    const entity=merge(current,value,{now:now()});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
  }

  async function clearSmoker(id){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const value=clearSessionSmoker(domain(current));
    const entity=merge(current,value,{now:now()});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
  }

  async function startSmoking(id,{stageId,at=now()}={}){
    const existingActive=await active();
    if(existingActive&&existingActive.id!==String(id||'')){
      throw new Error('ANOTHER_SMOKING_SESSION_ACTIVE');
    }
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const value=startSmokingSession(domain(current),{stageId,at});
    const entity=merge(current,value,{now:at});
    const saved=await queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
    notifyLifecycle(domain(current),value);
    return saved;
  }

  async function changeActiveStage(id,{
    stageId,
    at=now(),
    mutationBatchId=undefined
  }={}){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');

    const pendingStageBatch=(
      current.syncStatus===SyncStatus.PENDING_CREATE
      ||current.syncStatus===SyncStatus.PENDING_UPDATE
    )&&current.stageMutationBatchId
      ?current.stageMutationBatchId
      :null;
    const batch=mutationBatchId===undefined
      ?(pendingStageBatch||generatedMutationBatchId())
      :mutationBatchId;

    const value=changeActiveSmokingStage(domain(current),{
      stageId,
      at,
      mutationBatchId:batch
    });
    const entity=merge(current,value,{now:at});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
  }

  async function transition(id,nextStatus,{at=now()}={}){
    if(String(nextStatus||'').trim().toLowerCase()===SmokingSessionStatus.SMOKING){
      throw new Error('Use startSmoking to enter smoking state');
    }
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const previous=domain(current);
    const value=transitionSmokingSession(previous,nextStatus,{at});
    const entity=merge(current,value,{now:at});
    const saved=await queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
    notifyLifecycle(previous,value);
    return saved;
  }

  async function softDelete(id,{at=now()}={}){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('SMOKING_SESSION_CONFLICT');
    const value=softDeleteSmokingSession(domain(current),{at});
    const entity=merge(current,value,{now:at});
    return queue.enqueue(SMOKING_SESSION_ENTITY_TYPE,entity,'update');
  }

  async function markConflict(id,details){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    return queue.markConflict(SMOKING_SESSION_ENTITY_TYPE,current,details);
  }

  return Object.freeze({
    createDraft,resumeDraft,get,list,active,update,setSmoker,clearSmoker,startSmoking,changeActiveStage,transition,softDelete,markConflict
  });
}
